import {useCallback, useEffect, useMemo, useRef, useState} from "react"

interface UseShowMoreListOptions {
   initialCount?: number
   step?: number
}

interface UseShowMoreListResult<T> {
   visibleItems: T[]
   visibleCount: number
   totalCount: number
   remainingCount: number
   hasMore: boolean
   loadMore: () => void
}

/**
 * Encapsulates the "show more" pattern for rendering a subset of a list with an incremental button.
 */
export function useShowMoreList<T>(
   items: T[] = [],
   {initialCount = 5, step = 5}: UseShowMoreListOptions = {},
): UseShowMoreListResult<T> {
   const [visibleCount, setVisibleCount] = useState(() => Math.min(initialCount, items.length))
   // Track if the user has expanded to show all items at least once
   const expandedRef = useRef(false)

   useEffect(() => {
      setVisibleCount((prev) => {
         // If user expanded to all, keep all visible when list changes
         if (expandedRef.current) return items.length
         // Otherwise, preserve previous count but never below initialCount
         return Math.min(Math.max(prev, initialCount), items.length)
      })
   }, [items.length, initialCount])

   const visibleItems = useMemo(() => items.slice(0, visibleCount), [items, visibleCount])

   const totalCount = items.length
   const remainingCount = Math.max(totalCount - visibleCount, 0)
   const hasMore = remainingCount > 0

   const loadMore = useCallback(() => {
      setVisibleCount((prev) => {
         const next = Math.min(prev + step, totalCount)
         if (next >= totalCount) expandedRef.current = true
         return next
      })
   }, [step, totalCount])

   return {
      visibleItems,
      visibleCount,
      totalCount,
      remainingCount,
      hasMore,
      loadMore,
   }
}
